import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { handleEmailAdd } from 'utils/email-connect'
import { markStepCompleted } from 'pages/dashboard/pages/updater-agent/pages/ongoing-loads/utils/setupProgressStorage'
import { useAuth } from 'hooks/auth'
import { extensionAuthService, ExtensionApplication } from 'services/ExtensionAuthService'
import { getAuthToken, hasSessionOverride } from 'setup/SetupAxios'
import { showSuccess, showError } from 'utils/snackbar'

interface UseConnectDispatcherEmailOptions {
    /** Called after the email is connected and post-connect side effects run (e.g. mark the row active). */
    onComplete?: () => void
    /** Surface an error string to the caller (e.g. set local error text in the setup modal). */
    onError?: (message: string) => void
}

/**
 * Shared "connect dispatcher email" flow: opens the provider picker via handleEmailAdd and, on
 * success, navigates to the emails page, syncs auth state to the extension, marks the setup step
 * complete, and toasts. Extracted from DispatcherAgentSetupModal so the picker can be opened
 * directly (e.g. from the Sidebar "Enable" pill) without the intermediate intro modal.
 */
export const useConnectDispatcherEmail = ({ onComplete, onError }: UseConnectDispatcherEmailOptions = {}) => {
    const navigate = useNavigate()
    const { application, user } = useAuth()

    const connectEmail = useCallback(async () => {
        await handleEmailAdd(
            async () => {
                try {
                    navigate('/dispatcher/emails?type=all')

                    // Sync email enable state to the extension. Skip entirely while this tab is
                    // under a per-tab company-switch override: `user`/`application` below would
                    // already reflect the switched company, but the extension is deliberately
                    // pinned to the base session (see extensionSyncMiddleware.ts) — syncing here
                    // would pair the switched company's data with a token/identity the extension
                    // doesn't expect, the same inconsistency that guard exists to prevent.
                    const token = getAuthToken()
                    const extensionConnected = localStorage.getItem('numeo-extension-connected') === 'true'
                    if (token && extensionConnected && application && user && !hasSessionOverride()) {
                        await extensionAuthService.syncAuthState({
                            token,
                            userId: user.id,
                            email: user.email,
                            name: user.name,
                            emailTemplates: user.emailTemplates || { bodyTemplates: [], footer: '' },
                            nylasEmails:
                                application?.integrations?.nylas?.emails?.map((email) => ({
                                    id: email.email,
                                    email: email.email,
                                    isPrimary: false,
                                })) || [],
                            integrations: user.integrations || {},
                            application: {
                                projectName: application.projectName,
                                features: application.features || [],
                                integrations: application.integrations || {},
                                subscription: application.subscription,
                            } as ExtensionApplication,
                        })
                    }

                    onComplete?.()
                    showSuccess('Successfully connected email')
                    markStepCompleted('email', application?.projectName)
                } catch (error) {
                    console.error('Error fetching accounts after OAuth:', error)
                    showError(error instanceof Error ? error.message : 'An unexpected error occurred while connecting email')
                    onError?.('Successfully connected email, but failed to refresh account data')
                }
            },
            (error) => {
                console.error('Error during email OAuth:', error)
                showError(typeof error === 'string' ? error : 'An unexpected error occurred while connecting email')
                onError?.(typeof error === 'string' ? error : 'An unexpected error occurred while connecting email')
            },
            (message, type) => {
                if (type === 'danger') onError?.(message)
            }
        )
    }, [navigate, application, user, onComplete, onError])

    return { connectEmail }
}
